/**************************************************
 * components/dash/OrganGrid.js
 *
 * @author  LSJ
 * @since   1.0 (2025-02-28)
 * @version 1.0
 **************************************************/
import React, { useMemo, useState } from "react";
import { useDragScroll } from "@hooks";
import { getCurrentDate, gridMergeDataByKey, filterRoleList } from "@utils/dashboardApi";
import "./Dashboard.css";

/**
 * OrganGrid
 *
 * "프레젠테이션 컴포넌트"로서, [이해관계자] 대시보드 그리드를 렌더링합니다.
 * 전달받은 props를 이용하여 [테이블 헤더, 본문, 푸터], 그리고 셀 병합(rowSpan) 등 처리를 수행합니다.
 *
 * @param {Array} roleList    공통 코드: 안전 직무 (ComcdVO 리스트)
 * @param {Array} roleEmpInfo 회사 정보: 안전 직무 해당 사원 정보 (EmpInfoDTO 리스트)
 * @param {Array} data        이해관계자 데이터 (OrganDTO 리스트)
 * @param {Array} siteCodes   사업장 목록
 * @param {string} selectedSite 선택된 사업장 코드
 * @param {function} onSiteChange 사업장 변경 핸들러
 * @param {string} sizeCategory 회사 규모 ("1": 50인 미만, "2": 50인 이상)
 */
const OrganGrid = ({ roleList, roleEmpInfo, data, siteCodes, selectedSite, onSiteChange, sizeCategory }) => {
  // useDragScroll 훅을 사용하여 테이블 영역의 드래그-스크롤 기능 구현
  const { containerRef, eventHandlers, cursorStyle } = useDragScroll();

  // 사이트 옵션
  const siteOptions = useMemo(() => {
    const options = [{ key: '', value: '전체 사업장' }];

    if (siteCodes && siteCodes.length > 0) {
      const siteOptions = siteCodes.map(site => ({
        key: site.siteCode?.toString() || 'null',
        value: site.siteName || '미지정'
      }));

      // 정렬: 숫자 코드 → 오름차순
      siteOptions.sort((a, b) => {
        const na = Number(a.key), nb = Number(b.key);
        const ia = Number.isNaN(na), ib = Number.isNaN(nb);
        if (ia && ib) return a.value.localeCompare(b.value);
        if (ia) return 1;
        if (ib) return -1;
        return na - nb;
      });

      options.push(...siteOptions);
    }
    
    return options;
  }, [siteCodes]);

  const effectiveSelected = selectedSite || siteOptions[0]?.key || '';

  // 사업장별 데이터 필터링
  const filteredData = useMemo(() => {
    if (!effectiveSelected || effectiveSelected === '' || !data) {
      return [];
    }
    
    const selectedSiteCode = Number(effectiveSelected);
    
    const result = data.map((item) => {
      // OrganDTO의 empInfos에서 해당 사업장의 데이터만 필터링
      const filteredEmpInfos = item.empInfos.filter(emp => {
        return emp.siteCode === selectedSiteCode;
      });
      
      // empInfos가 있는 경우만 반환
      if (filteredEmpInfos.length > 0) {
        return {
          ...item,
          empInfos: filteredEmpInfos
        };
      }
      return null;
    }).filter(item => item !== null);
    
    return result;
  }, [data, effectiveSelected]);

  // 안전 직무 필터링 (회사 규모에 따라)
  const { filteredRoleList, filteredRoleEmpInfo } = filterRoleList(roleList, roleEmpInfo, sizeCategory);

  // 사업장별로 filteredRoleEmpInfo 필터링
  const siteFilteredRoleEmpInfo = useMemo(() => {
    if (!effectiveSelected || effectiveSelected === '') return filteredRoleEmpInfo;

    const selectedSiteCode = Number(effectiveSelected);

    const result = filteredRoleEmpInfo.filter(emp => {
      const empSiteCode = emp.siteCode != null ? Number(emp.siteCode) : null;
      return empSiteCode === selectedSiteCode;
    });
    
    return result;
  }, [filteredRoleEmpInfo, effectiveSelected]);

  // 안전 직무 필터링 (회사 규모에 따라)

  // ✅ 역할코드 -> 담당자 매핑 (find 반복 방지 + undefined 가드) - 사업장별 필터링된 데이터 사용
  const roleEmpMap = useMemo(() => {
    const m = new Map();
    (siteFilteredRoleEmpInfo || []).forEach((e) => m.set(e.role, e));
    return m;
  }, [siteFilteredRoleEmpInfo]);

  // 전체 사업장이 선택되었을 때만 메시지 표시
  const showMessage = !effectiveSelected || effectiveSelected === '';
  const message = "사업장을 선택하면 이해관계자 서명 정보를 확인할 수 있습니다.";

  // 데이터가 없으면 사용자에게 메시지 표시
  if (!data || data.length === 0) {
    return <div>데이터가 없습니다.</div>;
  }

  // 그룹화: 관리자 여부(hasMgr)을 기준으로 데이터 병합 (rowSpan 계산)
  // (선택) hasMgr 병합 전에 정렬해주면 rowSpan 안정적
  const allEmpInfos = (showMessage ? [] : filteredData).flatMap((item) => item.empInfos || []);
  const sortedEmpInfos = allEmpInfos.slice().sort((a, b) => Number(b.hasMgr) - Number(a.hasMgr));
  const mergeData = gridMergeDataByKey(sortedEmpInfos, "hasMgr");

  // 출력
  const handlePrint = () => {
    window.print();
  };
  return (
    <div className="og-wrap">
      {/* 출력 버튼 */}
      <div className="og-print-wrap no-print">
        <button className="og-print-btn no-print" onClick={handlePrint}>
          {"\u00A0\u00A0"}출{"\u00A0\u00A0\u00A0\u00A0"}력{"\u00A0\u00A0"}
        </button>
      </div>
      <h2 className="cg-title">[이해관계자 서명]</h2>
      
      {showMessage ? (
        <div className="text-center py-4">{message}</div>
      ) : (
        <div className="og-table-wrap" ref={containerRef} {...eventHandlers} style={{ cursor: cursorStyle }}>
          <table className="og-organ-table">
            {/* colgroup: 각 열의 너비를 지정 */}
            <colgroup>
              <col style={{ minWidth: '120px', width: '15%' }} />
              <col className="w-2" />
              <col className="w-2" />
              <col className="w-3" />
              <col className="w-3" />
              <col className="w-2" />
            </colgroup>
            <thead>
              {/* 첫 번째 헤더 행: 구분, 부서, 직급, 성명, 서명, 일자 */}
              <tr>
                <th style={{ background: 'var(--dash-accent-light)', color: 'var(--dash-text-secondary)' }}>구분</th>
                <th style={{ background: 'var(--dash-accent-light)', color: 'var(--dash-text-secondary)' }}>부서</th>
                <th style={{ background: 'var(--dash-accent-light)', color: 'var(--dash-text-secondary)' }}>직급</th>
                <th style={{ background: 'var(--dash-accent-light)', color: 'var(--dash-text-secondary)' }}>성명</th>
                <th style={{ background: 'var(--dash-accent-light)', color: 'var(--dash-text-secondary)' }}>서명</th>
                <th style={{ background: 'var(--dash-accent-light)', color: 'var(--dash-text-secondary)' }}>일자</th>
              </tr>
            </thead>
            {/* tbody: */}
            <tbody>
              {filteredRoleList.map((role) => {
                const emp = roleEmpMap.get(role.comdCode); // 없을 수 있음
                // 매칭 없는 역할은 출력하지 않으려면 아래 if 추가:
                // if (!emp) return null;

                return (
                  <tr key={role.comdCode}>
                    <td>{role.comdName}</td>
                    <td>{emp?.deptName ?? "-"}</td>
                    <td>{emp?.posName ?? "-"}</td>
                    <td>{emp?.empName ?? "-"}</td>
                    <td></td>
                    <td>{getCurrentDate()}</td>
                  </tr>
                );
              })}
              {/* ✅ 현장 관리자/근무자 병합 섹션 */}
              {mergeData.map((emp, idx) => (
                <tr key={idx}>
                  {emp.rowSpan > 0 && (
                    <td rowSpan={emp.rowSpan}>{emp.hasMgr ? "현장 관리자" : "근무자"}</td>
                  )}
                  <td>{emp?.deptName ?? "-"}</td>
                  <td>{emp?.posName ?? "-"}</td>
                  <td>{emp?.empName ?? "-"}</td>
                  <td></td>
                  <td>{getCurrentDate()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* 테이블 푸터 영역: 범례 및 추가 설명 */}
      <div className="table-footer no-print"></div>
    </div>
  );
};

export default OrganGrid;
